import { sum } from "lodash";

import {
  TOUCHPOINT_ALL_STATUSES_KEY,
  TOUCHPOINT_STATUS_OBSOLETE,
  TOUCHPOINT_TYPE_BULK
} from "constants/touchpoints";
import { formatValidQueryParamUrlCommaSeparated } from "utils/common";
import { formatPatientProductFilters } from "utils/patient-products.util";
import { formatPatientFilters } from "utils/patients.util";
import { createPaginatedEndpoint } from "utils/services.util";
import { formatTouchpoint } from "utils/touchpoints.utils";

import { emptySplitApi } from "./empty-split-api";
import {
  CreateBulkTouchpointInput,
  Touchpoint,
  TouchpointInput,
  type TouchpointPreview,
  TouchpointsCountByStatus,
  TouchpointsCountByStatusResponse,
  TouchpointsFilters,
  TouchpointsResponse
} from "./types/touchpoint.type";

const DEFAULT_QUERY =
  "{id,clinic{id,name,region{id,name}},patient{id,first_name,last_name,title,suffix},patient_products{id,display_name,ear,serial_number,status,manufacturer_warranty_expiration_date,loss_damage_warranty_expiration_date,assigned_tags,inventory_product{id,status,specification}},execution_time,notes,clinic_campaign{id,campaign{id,name,type}},communication_method,created_by,assigned_to{id,first_name,last_name,title,suffix},status}";

export const DEFAULT_TOUCHPOINTS_LIMIT = 25;

export const touchpointsApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getTouchpointsPaginated: createPaginatedEndpoint<
      TouchpointsFilters,
      Touchpoint[]
    >(builder, {
      queryFn: ({
        query = DEFAULT_QUERY,
        limit = DEFAULT_TOUCHPOINTS_LIMIT,
        offset = 0,
        search,
        ...restFilters
      }) => {
        const customFiltersString = formatValidQueryParamUrlCommaSeparated({
          ...restFilters,
          search: search && encodeURIComponent(search)
        });

        return `touchpoints/?query=${query}&limit=${limit}&offset=${offset}&${customFiltersString}`;
      },
      transformResponseFn: (response: TouchpointsResponse, _, filters) => {
        return response.results.map((touchpoint) =>
          formatTouchpoint(touchpoint, filters?.patient)
        );
      },
      providesTags: ["Touchpoints"]
    }),

    createTouchpoint: builder.mutation<Touchpoint, Partial<TouchpointInput>>({
      query: ({ patient, ...data }) => ({
        url: `patients/${patient}/touchpoints/`,
        method: "POST",
        body: data
      }),
      invalidatesTags: ["Touchpoints", "AssignedTouchpoints"]
    }),
    bulkCreateTouchpoints: builder.mutation<void, CreateBulkTouchpointInput>({
      query: (data) => {
        const patient_products_filter = data.patient_products_filter
          ? formatPatientProductFilters(data.patient_products_filter)
          : {};

        const patients_filter = data.patients_filter
          ? formatPatientFilters(data.patients_filter)
          : {};

        const appointments_filter = data.appointments_filter ?? {};

        return {
          url: `/touchpoints/bulk-create/`,
          method: "POST",
          body: {
            ...data,
            patient_products_filter,
            patients_filter,
            appointments_filter
          }
        };
      },
      invalidatesTags: ["Touchpoints", "AssignedTouchpoints"]
    }),
    updateTouchpoint: builder.mutation<Touchpoint, Partial<TouchpointInput>>({
      query: ({ patient, id, ...data }) => ({
        url: `patients/${patient}/touchpoints/${id}/`,
        method: "PATCH",
        body: data
      }),
      invalidatesTags: ["Touchpoints", "AssignedTouchpoints"]
    }),
    getTouchpoint: builder.query<Touchpoint, { id: number; query: string }>({
      query: ({ id, query }) => `touchpoints/${id}/?query=${query}`,
      transformResponse: (touchpoint: Touchpoint) => {
        return touchpoint;
      },
      providesTags: (result) => [
        {
          type: "Touchpoints" as const,
          id: result?.id
        }
      ]
    }),
    getTouchpointsCountByStatus: builder.query<
      TouchpointsCountByStatus,
      TouchpointsFilters
    >({
      query: (filters) => ({
        url: `/touchpoints/count/`,
        params: {
          ...filters,
          search: filters.search && encodeURIComponent(filters.search)
        }
      }),
      providesTags: ["Touchpoints"],
      transformResponse: (response: TouchpointsCountByStatusResponse) => {
        const countWithoutObsolete = Object.entries(response)
          .filter(([key]) => key !== TOUCHPOINT_STATUS_OBSOLETE.toString())
          .map(([_, value]) => value);

        return {
          ...response,
          [TOUCHPOINT_ALL_STATUSES_KEY]: sum(countWithoutObsolete)
        };
      }
    }),
    getAssignedTouchpointsCount: builder.query<
      number,
      {
        clinic: number | undefined;
        after_execution_time: string | null;
        before_execution_time: string | null;
      }
    >({
      query: (params) => ({
        params: params,
        url: "touchpoints/assigned/",
        method: "GET"
      }),
      providesTags: ["AssignedTouchpoints"],
      transformResponse: ({
        assigned_touchpoints_count
      }: {
        assigned_touchpoints_count: number;
      }) => {
        return assigned_touchpoints_count;
      }
    }),
    getBulkTouchpointsCount: builder.query<
      number,
      {
        clinic: number | undefined;
        after_execution_time: string | null;
        before_execution_time: string | null;
      }
    >({
      query: (params) => ({
        params: { ...params, type: TOUCHPOINT_TYPE_BULK, limit: 0 },
        url: "touchpoints/",
        method: "GET"
      }),
      providesTags: ["BulkTouchpoints"],
      transformResponse: ({ count }: { count: number }) => {
        return count;
      }
    }),
    getTouchpointPreview: builder.query<
      TouchpointPreview,
      TouchpointPreview & { patient_id: number }
    >({
      query: ({ patient_id, ...body }) => ({
        url: `patients/${patient_id}/touchpoints/preview/`,
        method: "POST",
        body
      }),
      providesTags: ["TouchpointsPreview"]
    })
  })
});

export const {
  useGetTouchpointQuery,
  useCreateTouchpointMutation,
  useUpdateTouchpointMutation,
  useGetAssignedTouchpointsCountQuery,
  useGetBulkTouchpointsCountQuery,
  useGetTouchpointsCountByStatusQuery,
  useBulkCreateTouchpointsMutation,
  useGetTouchpointPreviewQuery
} = touchpointsApi;
